import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Building2, Layers, Wallet, MapPin, Plus, Edit3, Trash2, X, Save, RefreshCw, Loader2, ShieldCheck, Link2, GitBranch, ArrowRight, BarChart3, Leaf, Download,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/gov`;

// Extract a readable error message from an Axios error. FastAPI 422 responses
// come back with `detail` as an array of {loc, msg, ...} objects — join by "; ".
const errMsg = (e) => {
  const detail = e?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => (typeof d === 'string' ? d : d?.msg || JSON.stringify(d))).join('; ');
  }
  if (typeof detail === 'string') return detail;
  return e?.message || '';
};

// Normalize blank / whitespace-only optional email fields to null before submit
// so the backend's EmailStr validator doesn't reject empty strings.
const normalizePayload = (data) => {
  const EMAIL_KEYS = ['manager_email', 'budget_owner_email', 'contact_email'];
  const out = { ...data };
  EMAIL_KEYS.forEach((k) => {
    if (k in out) {
      const v = out[k];
      if (typeof v === 'string' && v.trim() === '') out[k] = null;
      else if (v === undefined) out[k] = null;
    }
  });
  return out;
};

const TABS = [
  { id: 'divisions',   label: 'Divisions',           icon: Building2 },
  { id: 'departments', label: 'Departments',         icon: Layers },
  { id: 'workflows',   label: 'Workflows',           icon: GitBranch },
  { id: 'costCenters', label: 'Cost Centers',        icon: Wallet },
  { id: 'locations',   label: 'Delivery Locations',  icon: MapPin },
  { id: 'reports',     label: 'Reports',             icon: BarChart3 },
];

const GovGovernance = () => {
  const { user } = useAuth();
  const agencyId = user?.agency_id;
  const [tab, setTab] = useState('divisions');
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = () => setRefreshKey((k) => k + 1);

  if (!agencyId) return <div className="p-6 text-sm text-slate-500">Your account is not linked to an agency.</div>;

  return (
    <div className="space-y-4" data-testid="gov-governance-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center gap-2">
            <ShieldCheck className="h-7 w-7 text-blue-700" /> Governance
          </h1>
          <p className="text-sm text-slate-500">Manage divisions, departments, cost centers, delivery locations, and their assignments.</p>
        </div>
        <Button variant="outline" onClick={bump} data-testid="gov-refresh"><RefreshCw className="h-4 w-4" /></Button>
      </div>

      <div className="flex gap-1 border-b -mb-px overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`gov-tab-${t.id}`}
              className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 transition ${active ? 'border-blue-700 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
              <Icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'divisions'   && <DivisionsTab agencyId={agencyId} refreshKey={refreshKey} onDataChange={bump} />}
      {tab === 'departments' && <DepartmentsTab agencyId={agencyId} refreshKey={refreshKey} onDataChange={bump} />}
      {tab === 'workflows'   && <WorkflowsTab agencyId={agencyId} refreshKey={refreshKey} onDataChange={bump} />}
      {tab === 'costCenters' && <CostCentersTab agencyId={agencyId} refreshKey={refreshKey} onDataChange={bump} />}
      {tab === 'locations'   && <LocationsTab agencyId={agencyId} refreshKey={refreshKey} onDataChange={bump} />}
      {tab === 'reports'     && <ReportsTab agencyId={agencyId} refreshKey={refreshKey} />}
    </div>
  );
};

// ============================================================================
// Divisions tab
// ============================================================================
const DivisionsTab = ({ agencyId, refreshKey, onDataChange }) => {
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(null);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/agencies/${agencyId}/divisions`).then((r) => setDivisions(r.data || []))
      .catch((e) => toast({ title: 'Load failed', description: errMsg(e), variant: 'destructive' }))
      .finally(() => setLoading(false));
  }, [agencyId, refreshKey]);

  const save = async () => {
    const payload = normalizePayload(dlg.data);
    try {
      if (dlg.mode === 'create') await axios.post(`${API}/agencies/${agencyId}/divisions`, payload);
      else await axios.put(`${API}/divisions/${payload.id}`, payload);
      toast({ title: 'Saved' }); setDlg(null); onDataChange();
    } catch (e) { toast({ title: 'Save failed', description: errMsg(e), variant: 'destructive' }); }
  };
  const del = async (d) => {
    if (!window.confirm(`Delete "${d.name}"?`)) return;
    try { await axios.delete(`${API}/divisions/${d.id}`); toast({ title: 'Deleted' }); onDataChange(); }
    catch (e) { toast({ title: 'Delete failed', description: errMsg(e), variant: 'destructive' }); }
  };

  return (
    <Card className="border-2">
      <CardHeader className="flex flex-row items-center justify-between space-y-0"><CardTitle className="text-base">Divisions</CardTitle>
        <Button size="sm" onClick={() => setDlg({ mode: 'create', data: { name: '', code: '', description: '', is_active: true } })} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="new-division">
          <Plus className="h-4 w-4 mr-1" /> New Division
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? <Loader2 className="h-6 w-6 animate-spin text-blue-700 mx-auto" /> : divisions.length === 0 ? (
          <div className="text-slate-400 text-sm py-4">No divisions yet.</div>
        ) : (
          <Table headers={['Name', 'Code', 'Description', '']}>
            {divisions.map((d) => (
              <tr key={d.id} className="border-t" data-testid={`div-row-${d.id}`}>
                <td className="p-2 font-medium">{d.name}</td>
                <td className="p-2 font-mono text-xs">{d.code || '—'}</td>
                <td className="p-2 text-xs text-slate-500">{d.description || '—'}</td>
                <td className="p-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setDlg({ mode: 'edit', data: { ...d } })}><Edit3 className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => del(d)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </CardContent>
      {dlg && <FormDialog title={`${dlg.mode === 'create' ? 'New' : 'Edit'} Division`} onClose={() => setDlg(null)} onSave={save}>
        <Field label="Name *"><Input value={dlg.data.name} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, name: e.target.value } })} data-testid="dlg-div-name" /></Field>
        <Field label="Code"><Input value={dlg.data.code || ''} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, code: e.target.value } })} /></Field>
        <Field label="Description"><Textarea rows={2} value={dlg.data.description || ''} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, description: e.target.value } })} /></Field>
      </FormDialog>}
    </Card>
  );
};

// ============================================================================
// Departments tab (grouped by division)
// ============================================================================
const DepartmentsTab = ({ agencyId, refreshKey, onDataChange }) => {
  const [divisions, setDivisions] = useState([]);
  const [byDiv, setByDiv] = useState({});
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const divRes = await axios.get(`${API}/agencies/${agencyId}/divisions`);
        const divs = divRes.data || [];
        if (!alive) return;
        setDivisions(divs);
        const map = {};
        for (const d of divs) {
          const r = await axios.get(`${API}/divisions/${d.id}/departments`);
          map[d.id] = r.data || [];
        }
        if (alive) setByDiv(map);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [agencyId, refreshKey]);

  const save = async () => {
    const payload = normalizePayload(dlg.data);
    try {
      if (dlg.mode === 'create') await axios.post(`${API}/divisions/${dlg.divisionId}/departments`, payload);
      else await axios.put(`${API}/departments/${payload.id}`, payload);
      toast({ title: 'Saved' }); setDlg(null); onDataChange();
    } catch (e) { toast({ title: 'Save failed', description: errMsg(e), variant: 'destructive' }); }
  };
  const del = async (dept) => {
    if (!window.confirm(`Delete "${dept.name}"?`)) return;
    try { const r = await axios.delete(`${API}/departments/${dept.id}`); toast({ title: r.data.deactivated ? 'Deactivated' : 'Deleted' }); onDataChange(); }
    catch (e) { toast({ title: 'Delete failed', description: errMsg(e), variant: 'destructive' }); }
  };

  if (loading) return <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-700" /></div>;

  return (
    <div className="space-y-3">
      {divisions.map((d) => (
        <Card key={d.id} className="border-2" data-testid={`dept-section-${d.id}`}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base flex items-center gap-2"><Building2 className="h-4 w-4 text-slate-500" /> {d.name} · Departments</CardTitle>
            <Button size="sm" variant="outline" onClick={() => setDlg({ divisionId: d.id, mode: 'create', data: { name: '', code: '', description: '', manager_name: '', manager_email: '', is_active: true } })} data-testid={`add-dept-${d.id}`}><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
          </CardHeader>
          <CardContent>
            {(byDiv[d.id] || []).length === 0 ? <div className="text-xs text-slate-400 py-2">No departments.</div> : (
              <Table headers={['Name', 'Code', 'Manager', 'Status', '']}>
                {byDiv[d.id].map((p) => (
                  <tr key={p.id} className="border-t" data-testid={`dept-row-${p.id}`}>
                    <td className="p-2 font-medium">{p.name}</td>
                    <td className="p-2 font-mono text-xs">{p.code || '—'}</td>
                    <td className="p-2 text-xs text-slate-500">{p.manager_name || '—'}</td>
                    <td className="p-2">{p.is_active ? <span className="text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">Active</span> : <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">Inactive</span>}</td>
                    <td className="p-2 text-right">
                      <Button size="sm" variant="ghost" onClick={() => setDlg({ divisionId: d.id, mode: 'edit', data: { ...p } })}><Edit3 className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => del(p)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </td>
                  </tr>
                ))}
              </Table>
            )}
          </CardContent>
        </Card>
      ))}
      {dlg && <FormDialog title={`${dlg.mode === 'create' ? 'New' : 'Edit'} Department`} onClose={() => setDlg(null)} onSave={save}>
        <Field label="Name *"><Input value={dlg.data.name} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, name: e.target.value } })} data-testid="dlg-dept-name" /></Field>
        <Field label="Code"><Input value={dlg.data.code || ''} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, code: e.target.value } })} /></Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Manager name"><Input value={dlg.data.manager_name || ''} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, manager_name: e.target.value } })} /></Field>
          <Field label="Manager email"><Input type="email" value={dlg.data.manager_email || ''} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, manager_email: e.target.value } })} /></Field>
        </div>
        <Field label="Description"><Textarea rows={2} value={dlg.data.description || ''} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, description: e.target.value } })} /></Field>
      </FormDialog>}
    </div>
  );
};

// ============================================================================
// Cost Centers tab (agency-level with assignment editor)
// ============================================================================
const CostCentersTab = ({ agencyId, refreshKey, onDataChange }) => {
  const [ccs, setCcs] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [departments, setDepartments] = useState([]);   // flat list with divisionName
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(null);
  const [assignDlg, setAssignDlg] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ccRes, dvRes] = await Promise.all([
        axios.get(`${API}/agencies/${agencyId}/cost-centers`),
        axios.get(`${API}/agencies/${agencyId}/divisions`),
      ]);
      const divs = dvRes.data || [];
      setDivisions(divs);
      // Flatten departments across all divisions
      const flat = [];
      await Promise.all(divs.map(async (d) => {
        const r = await axios.get(`${API}/divisions/${d.id}/departments`);
        (r.data || []).forEach((dep) => flat.push({ ...dep, divisionName: d.name }));
      }));
      setDepartments(flat);
      setCcs(ccRes.data || []);
    } catch (e) { toast({ title: 'Load failed', description: errMsg(e), variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [agencyId]);

  useEffect(() => { fetchAll(); }, [fetchAll, refreshKey]);

  const save = async () => {
    const payload = normalizePayload(dlg.data);
    try {
      if (dlg.mode === 'create') await axios.post(`${API}/agencies/${agencyId}/cost-centers`, payload);
      else await axios.put(`${API}/cost-centers/${payload.id}`, payload);
      toast({ title: 'Saved' }); setDlg(null); onDataChange();
    } catch (e) { toast({ title: 'Save failed', description: errMsg(e), variant: 'destructive' }); }
  };
  const del = async (c) => {
    if (!window.confirm(`Delete "${c.code} — ${c.name}"?`)) return;
    try { const r = await axios.delete(`${API}/cost-centers/${c.id}`); toast({ title: r.data.deactivated ? 'Deactivated' : 'Deleted' }); onDataChange(); }
    catch (e) { toast({ title: 'Delete failed', description: errMsg(e), variant: 'destructive' }); }
  };
  const saveAssignments = async () => {
    try {
      await axios.put(`${API}/cost-centers/${assignDlg.id}/assignments`, { assigned_scopes: assignDlg.data });
      toast({ title: 'Assignments updated' }); setAssignDlg(null); onDataChange();
    } catch (e) { toast({ title: 'Save failed', description: errMsg(e), variant: 'destructive' }); }
  };

  const scopeLabel = (s) => {
    if (s.scope === 'division') return divisions.find((d) => d.id === s.scope_id)?.name || 'Division';
    return departments.find((d) => d.id === s.scope_id)?.name || 'Department';
  };

  return (
    <Card className="border-2">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Agency Cost Centers</CardTitle>
        <Button size="sm" onClick={() => setDlg({ mode: 'create', data: { code: '', name: '', gl_account: '', budget_owner_name: '', budget_owner_email: '', description: '', is_active: true, assigned_scopes: [] } })} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="new-cc">
          <Plus className="h-4 w-4 mr-1" /> New Cost Center
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? <Loader2 className="h-6 w-6 animate-spin text-blue-700 mx-auto" /> : ccs.length === 0 ? (
          <div className="text-slate-400 text-sm py-4">No cost centers yet.</div>
        ) : (
          <Table headers={['Code', 'Name', 'GL Acct', 'Assignments', 'Status', '']}>
            {ccs.map((c) => (
              <tr key={c.id} className="border-t" data-testid={`cc-row-${c.id}`}>
                <td className="p-2 font-mono text-xs">{c.code}</td>
                <td className="p-2 font-medium">{c.name}</td>
                <td className="p-2 text-xs text-slate-500">{c.gl_account || '—'}</td>
                <td className="p-2">
                  {(c.assigned_scopes || []).length === 0 ? (
                    <span className="text-[11px] text-amber-700">Unassigned</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {c.assigned_scopes.map((s) => (
                        <span key={`${s.scope}-${s.scope_id}`} className={`text-[10px] px-1.5 py-0.5 rounded ${s.scope === 'division' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>
                          {s.scope === 'division' ? '📁' : '👥'} {scopeLabel(s)}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td className="p-2">{c.is_active ? <span className="text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">Active</span> : <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">Inactive</span>}</td>
                <td className="p-2 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => setAssignDlg({ id: c.id, code: c.code, name: c.name, data: [...(c.assigned_scopes || [])] })} data-testid={`assign-cc-${c.id}`}><Link2 className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => setDlg({ mode: 'edit', data: { ...c } })}><Edit3 className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => del(c)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </CardContent>

      {dlg && <FormDialog title={`${dlg.mode === 'create' ? 'New' : 'Edit'} Cost Center`} onClose={() => setDlg(null)} onSave={save}>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Code *"><Input value={dlg.data.code} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, code: e.target.value } })} placeholder="e.g. CC-100" data-testid="dlg-cc-code" /></Field>
          <Field label="GL account"><Input value={dlg.data.gl_account || ''} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, gl_account: e.target.value } })} /></Field>
        </div>
        <Field label="Name *"><Input value={dlg.data.name} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, name: e.target.value } })} data-testid="dlg-cc-name" /></Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Budget owner"><Input value={dlg.data.budget_owner_name || ''} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, budget_owner_name: e.target.value } })} /></Field>
          <Field label="Owner email"><Input type="email" value={dlg.data.budget_owner_email || ''} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, budget_owner_email: e.target.value } })} /></Field>
        </div>
        <Field label="Description"><Textarea rows={2} value={dlg.data.description || ''} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, description: e.target.value } })} /></Field>
      </FormDialog>}

      {assignDlg && (
        <AssignDialog assignDlg={assignDlg} setAssignDlg={setAssignDlg} divisions={divisions} departments={departments} onSave={saveAssignments} />
      )}
    </Card>
  );
};

const AssignDialog = ({ assignDlg, setAssignDlg, divisions, departments, onSave }) => {
  const has = (scope, id) => assignDlg.data.some((s) => s.scope === scope && s.scope_id === id);
  const toggle = (scope, id) => {
    const next = has(scope, id)
      ? assignDlg.data.filter((s) => !(s.scope === scope && s.scope_id === id))
      : [...assignDlg.data, { scope, scope_id: id }];
    setAssignDlg({ ...assignDlg, data: next });
  };
  return (
    <FormDialog title={`Assignments · ${assignDlg.code} — ${assignDlg.name}`} onClose={() => setAssignDlg(null)} onSave={onSave}>
      <p className="text-xs text-slate-500">Pick the Divisions and/or Departments that may use this Cost Center. Purchasers will see it in their order form when their Department OR Division is checked here.</p>

      <div>
        <div className="text-xs font-semibold text-slate-700 uppercase mb-1">Divisions</div>
        <div className="space-y-1 max-h-40 overflow-y-auto border rounded p-2 bg-slate-50">
          {divisions.length === 0 && <div className="text-xs text-slate-400">No divisions.</div>}
          {divisions.map((d) => (
            <label key={d.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`assign-div-${d.id}`}>
              <input type="checkbox" checked={has('division', d.id)} onChange={() => toggle('division', d.id)} />
              <span className="font-medium">{d.name}</span>
              {d.code && <code className="text-[10px] bg-white px-1 rounded border font-mono">{d.code}</code>}
            </label>
          ))}
        </div>
      </div>

      <div>
        <div className="text-xs font-semibold text-slate-700 uppercase mb-1">Departments</div>
        <div className="space-y-1 max-h-52 overflow-y-auto border rounded p-2 bg-slate-50">
          {departments.length === 0 && <div className="text-xs text-slate-400">No departments.</div>}
          {departments.map((d) => (
            <label key={d.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`assign-dept-${d.id}`}>
              <input type="checkbox" checked={has('department', d.id)} onChange={() => toggle('department', d.id)} />
              <span className="font-medium">{d.name}</span>
              <span className="text-[10px] text-slate-400">({d.divisionName})</span>
            </label>
          ))}
        </div>
      </div>
    </FormDialog>
  );
};

// ============================================================================
// Workflows tab — one workflow per Department
// ============================================================================
const ROLE_OPTIONS = [
  { v: 'approver',        l: 'Approver' },
  { v: 'division_admin',  l: 'Division Administrator' },
  { v: 'finance_officer', l: 'Finance Officer' },
  { v: 'agency_admin',    l: 'Agency Administrator' },
];

const WorkflowsTab = ({ agencyId, refreshKey, onDataChange }) => {
  const [divisions, setDivisions] = useState([]);
  const [byDiv, setByDiv] = useState({});                // divisionId → [depts]
  const [wfByDept, setWfByDept] = useState({});          // deptId → workflow|null
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const divRes = await axios.get(`${API}/agencies/${agencyId}/divisions`);
      const divs = divRes.data || [];
      setDivisions(divs);
      const dMap = {}; const wMap = {};
      for (const d of divs) {
        const r = await axios.get(`${API}/divisions/${d.id}/departments`);
        dMap[d.id] = r.data || [];
        for (const dept of dMap[d.id]) {
          const wf = await axios.get(`${API}/departments/${dept.id}/workflow`);
          wMap[dept.id] = wf.data || null;
        }
      }
      setByDiv(dMap); setWfByDept(wMap);
    } catch (e) { toast({ title: 'Load failed', description: errMsg(e), variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [agencyId]);

  useEffect(() => { loadAll(); }, [loadAll, refreshKey]);

  const openEditor = (deptId, deptName, existing) => {
    setDlg({
      deptId, deptName,
      data: existing
        ? { name: existing.name, description: existing.description || '',
            steps: existing.steps.map((s) => ({ ...s })),
            fast_track_threshold: existing.fast_track_threshold || '',
            fast_track_skip_steps: existing.fast_track_skip_steps || '' }
        : { name: 'Standard Approval', description: '',
            steps: [{ step: 1, role: 'approver', label: 'Validator' }],
            fast_track_threshold: '', fast_track_skip_steps: '' },
    });
  };

  const save = async () => {
    try {
      const payload = {
        name: dlg.data.name,
        description: dlg.data.description || null,
        steps: dlg.data.steps.map((s, i) => ({ step: i + 1, role: s.role, label: s.label })),
        fast_track_threshold: dlg.data.fast_track_threshold ? Number(dlg.data.fast_track_threshold) : null,
        fast_track_skip_steps: dlg.data.fast_track_skip_steps ? Number(dlg.data.fast_track_skip_steps) : null,
      };
      await axios.post(`${API}/departments/${dlg.deptId}/workflow`, payload);
      toast({ title: 'Workflow saved' });
      setDlg(null); onDataChange();
    } catch (e) { toast({ title: 'Save failed', description: errMsg(e), variant: 'destructive' }); }
  };

  const del = async (wf) => {
    if (!window.confirm(`Delete workflow "${wf.name}"?`)) return;
    try {
      await axios.delete(`${API}/workflows/${wf.id}`);
      toast({ title: 'Workflow removed' }); onDataChange();
    } catch (e) { toast({ title: 'Delete failed', description: errMsg(e), variant: 'destructive' }); }
  };

  if (loading) return <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-700" /></div>;

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500 -mt-2">A workflow defines the sequence of roles that must approve each order for a department. If a role rejects, the order is sent back to the purchaser.</p>
      {divisions.map((d) => (
        <Card key={d.id} className="border-2">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Building2 className="h-4 w-4 text-slate-500" /> {d.name}</CardTitle></CardHeader>
          <CardContent>
            {(byDiv[d.id] || []).length === 0 ? <div className="text-xs text-slate-400 py-2">No departments in this division.</div> : (
              <div className="space-y-2">
                {byDiv[d.id].map((dept) => {
                  const wf = wfByDept[dept.id];
                  return (
                    <div key={dept.id} className="border rounded p-3 flex items-start justify-between gap-4" data-testid={`wf-row-${dept.id}`}>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-slate-800 mb-1">{dept.name} {dept.code && <code className="text-[10px] bg-slate-100 px-1 rounded">{dept.code}</code>}</div>
                        {wf ? (
                          <>
                            <div className="flex items-center flex-wrap gap-1 text-xs">
                              <span className="text-slate-500 mr-1">{wf.name} · </span>
                              {wf.steps.map((s, i) => (
                                <React.Fragment key={s.step}>
                                  <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded font-medium">{s.label}</span>
                                  {i < wf.steps.length - 1 && <ArrowRight className="h-3 w-3 text-slate-400" />}
                                </React.Fragment>
                              ))}
                            </div>
                            {wf.fast_track_threshold && wf.fast_track_skip_steps && (
                              <div className="mt-1 text-[11px] bg-emerald-50 text-emerald-800 border border-emerald-200 rounded px-2 py-0.5 inline-block">
                                ⚡ Fast-track: orders ≤ ${Number(wf.fast_track_threshold).toLocaleString()} skip the first {wf.fast_track_skip_steps} step{wf.fast_track_skip_steps > 1 ? 's' : ''}
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="text-xs text-amber-700">No workflow — default legacy approval will be used.</div>
                        )}
                      </div>
                      <div className="flex gap-1 items-center">
                        <Button size="sm" variant="outline" onClick={() => openEditor(dept.id, dept.name, wf)} data-testid={`edit-wf-${dept.id}`}>
                          {wf ? <><Edit3 className="h-3.5 w-3.5 mr-1" /> Edit</> : <><Plus className="h-3.5 w-3.5 mr-1" /> Create</>}
                        </Button>
                        {wf && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => del(wf)}><Trash2 className="h-3.5 w-3.5" /></Button>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      ))}

      {dlg && (
        <FormDialog title={`Workflow · ${dlg.deptName}`} onClose={() => setDlg(null)} onSave={save}>
          <Field label="Workflow name *"><Input value={dlg.data.name} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, name: e.target.value } })} data-testid="wf-name" /></Field>
          <Field label="Description"><Textarea rows={2} value={dlg.data.description} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, description: e.target.value } })} /></Field>
          <div>
            <div className="text-xs font-semibold text-slate-700 uppercase mb-1 flex items-center justify-between">
              <span>Approval Chain</span>
              <Button size="sm" variant="outline" onClick={() => setDlg({ ...dlg, data: { ...dlg.data, steps: [...dlg.data.steps, { step: dlg.data.steps.length + 1, role: 'approver', label: '' }] } })} data-testid="wf-add-step">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add step
              </Button>
            </div>
            <div className="space-y-2">
              {dlg.data.steps.map((s, i) => (
                <div key={i} className="flex items-center gap-2 p-2 border rounded bg-slate-50" data-testid={`wf-step-${i + 1}`}>
                  <span className="w-6 h-6 rounded-full bg-blue-700 text-white text-xs flex items-center justify-center flex-shrink-0">{i + 1}</span>
                  <select value={s.role} onChange={(e) => {
                    const steps = [...dlg.data.steps]; steps[i] = { ...steps[i], role: e.target.value };
                    setDlg({ ...dlg, data: { ...dlg.data, steps } });
                  }} className="border rounded px-2 py-1 text-sm bg-white" data-testid={`wf-role-${i + 1}`}>
                    {ROLE_OPTIONS.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
                  </select>
                  <Input placeholder="Display label (e.g. Validator)" value={s.label} onChange={(e) => {
                    const steps = [...dlg.data.steps]; steps[i] = { ...steps[i], label: e.target.value };
                    setDlg({ ...dlg, data: { ...dlg.data, steps } });
                  }} className="flex-1" data-testid={`wf-label-${i + 1}`} />
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => {
                    const steps = dlg.data.steps.filter((_, k) => k !== i);
                    if (steps.length === 0) { toast({ title: 'At least one step is required', variant: 'destructive' }); return; }
                    setDlg({ ...dlg, data: { ...dlg.data, steps } });
                  }} data-testid={`wf-remove-${i + 1}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t pt-3">
            <div className="text-xs font-semibold text-slate-700 uppercase mb-2 flex items-center gap-1">⚡ Fast-Track (optional)</div>
            <p className="text-[11px] text-slate-500 mb-2">Small orders can skip the first N steps and go straight to Finance. Leave both blank to disable.</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Threshold ($)">
                <Input type="number" min="0" step="0.01" value={dlg.data.fast_track_threshold} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, fast_track_threshold: e.target.value } })} placeholder="e.g. 500" data-testid="wf-ft-threshold" />
              </Field>
              <Field label="Skip first N steps">
                <Input type="number" min="1" max={Math.max(1, dlg.data.steps.length - 1)} value={dlg.data.fast_track_skip_steps} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, fast_track_skip_steps: e.target.value } })} placeholder="e.g. 1" data-testid="wf-ft-skip" />
              </Field>
            </div>
          </div>
        </FormDialog>
      )}
    </div>
  );
};

// ============================================================================
// Delivery Locations tab (grouped by division)
// ============================================================================
const LocationsTab = ({ agencyId, refreshKey, onDataChange }) => {
  const [divisions, setDivisions] = useState([]);
  const [byDiv, setByDiv] = useState({});
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const divRes = await axios.get(`${API}/agencies/${agencyId}/divisions`);
        const divs = divRes.data || [];
        if (!alive) return;
        setDivisions(divs);
        const map = {};
        for (const d of divs) {
          const r = await axios.get(`${API}/divisions/${d.id}/delivery-locations`);
          map[d.id] = r.data || [];
        }
        if (alive) setByDiv(map);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [agencyId, refreshKey]);

  const save = async () => {
    const payload = normalizePayload(dlg.data);
    try {
      if (dlg.mode === 'create') await axios.post(`${API}/divisions/${dlg.divisionId}/delivery-locations`, payload);
      else await axios.put(`${API}/delivery-locations/${payload.id}`, payload);
      toast({ title: 'Saved' }); setDlg(null); onDataChange();
    } catch (e) { toast({ title: 'Save failed', description: errMsg(e), variant: 'destructive' }); }
  };
  const del = async (loc) => {
    if (!window.confirm(`Delete "${loc.name}"?`)) return;
    try { const r = await axios.delete(`${API}/delivery-locations/${loc.id}`); toast({ title: r.data.deactivated ? 'Deactivated' : 'Deleted' }); onDataChange(); }
    catch (e) { toast({ title: 'Delete failed', description: errMsg(e), variant: 'destructive' }); }
  };

  if (loading) return <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-700" /></div>;

  return (
    <div className="space-y-3">
      {divisions.map((d) => (
        <Card key={d.id} className="border-2" data-testid={`loc-section-${d.id}`}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base flex items-center gap-2"><Building2 className="h-4 w-4 text-slate-500" /> {d.name} · Delivery Locations</CardTitle>
            <Button size="sm" variant="outline" onClick={() => setDlg({ divisionId: d.id, mode: 'create', data: { name: '', address_line1: '', address_line2: '', city: '', state: '', zip: '', country: 'USA', contact_name: '', contact_phone: '', contact_email: '', delivery_instructions: '', is_default: false, is_active: true } })} data-testid={`add-loc-${d.id}`}><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
          </CardHeader>
          <CardContent>
            {(byDiv[d.id] || []).length === 0 ? <div className="text-xs text-slate-400 py-2">No locations.</div> : (
              <Table headers={['Name', 'Address', 'Contact', 'Status', '']}>
                {byDiv[d.id].map((l) => (
                  <tr key={l.id} className="border-t" data-testid={`loc-row-${l.id}`}>
                    <td className="p-2">{l.name}{l.is_default && <span className="ml-1 text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded uppercase">Default</span>}</td>
                    <td className="p-2 text-xs text-slate-500">{l.address_line1}, {l.city}, {l.state} {l.zip}</td>
                    <td className="p-2 text-xs text-slate-500">{l.contact_name || '—'}</td>
                    <td className="p-2">{l.is_active ? <span className="text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">Active</span> : <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">Inactive</span>}</td>
                    <td className="p-2 text-right">
                      <Button size="sm" variant="ghost" onClick={() => setDlg({ divisionId: d.id, mode: 'edit', data: { ...l } })}><Edit3 className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => del(l)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </td>
                  </tr>
                ))}
              </Table>
            )}
          </CardContent>
        </Card>
      ))}
      {dlg && <FormDialog title={`${dlg.mode === 'create' ? 'New' : 'Edit'} Delivery Location`} onClose={() => setDlg(null)} onSave={save}>
        <Field label="Name *"><Input value={dlg.data.name} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, name: e.target.value } })} data-testid="dlg-loc-name" /></Field>
        <Field label="Address line 1 *"><Input value={dlg.data.address_line1} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, address_line1: e.target.value } })} /></Field>
        <Field label="Address line 2"><Input value={dlg.data.address_line2 || ''} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, address_line2: e.target.value } })} /></Field>
        <div className="grid grid-cols-3 gap-2">
          <Field label="City *"><Input value={dlg.data.city} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, city: e.target.value } })} /></Field>
          <Field label="State *"><Input value={dlg.data.state} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, state: e.target.value } })} /></Field>
          <Field label="ZIP *"><Input value={dlg.data.zip} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, zip: e.target.value } })} /></Field>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Contact name"><Input value={dlg.data.contact_name || ''} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, contact_name: e.target.value } })} /></Field>
          <Field label="Contact phone"><Input value={dlg.data.contact_phone || ''} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, contact_phone: e.target.value } })} /></Field>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!dlg.data.is_default} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, is_default: e.target.checked } })} /> Default for this division</label>
      </FormDialog>}
    </div>
  );
};

// ============================================================================
// Reports tab — monthly / quarterly spend by department, cost center, delivery
// location, plus Total Green Spend. Includes a CSV export button.
// ============================================================================
const ReportsTab = ({ agencyId, refreshKey }) => {
  const currentYear = new Date().getUTCFullYear();
  const currentMonth = new Date().getUTCMonth() + 1;
  const currentQuarter = Math.ceil(currentMonth / 3);
  const [period, setPeriod] = useState('monthly');
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(currentMonth);
  const [quarter, setQuarter] = useState(currentQuarter);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period, year };
      if (period === 'monthly') params.month = month;
      else params.quarter = quarter;
      const res = await axios.get(`${API}/agencies/${agencyId}/reports/spend`, { params });
      setData(res.data);
    } catch (e) {
      toast({ title: 'Report failed', description: errMsg(e), variant: 'destructive' });
      setData(null);
    } finally { setLoading(false); }
  }, [agencyId, period, year, month, quarter]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const exportCsv = () => {
    if (!data) return;
    const rows = [];
    const label = data.label || '';
    // Summary block
    rows.push(['USKE Governance Spend Report']);
    rows.push(['Period', label]);
    rows.push(['Total orders', data.totals.order_count]);
    rows.push(['Total spend', data.totals.total_spend]);
    rows.push(['Green spend', data.totals.green_spend]);
    rows.push(['Green %', `${data.totals.green_pct}%`]);
    rows.push([]);
    rows.push(['Spend by Department']);
    rows.push(['Division', 'Department', 'Orders', 'Total Spend', 'Green Spend']);
    (data.by_department || []).forEach((r) => rows.push([r.division_name || '', r.department_name, r.order_count, r.total_spend, r.green_spend]));
    rows.push([]);
    rows.push(['Spend by Cost Center']);
    rows.push(['Code', 'Name', 'GL Account', 'Orders', 'Total Spend', 'Green Spend']);
    (data.by_cost_center || []).forEach((r) => rows.push([r.code, r.name, r.gl_account || '', r.order_count, r.total_spend, r.green_spend]));
    rows.push([]);
    rows.push(['Spend by Delivery Location']);
    rows.push(['Name', 'Address', 'City', 'State', 'ZIP', 'Orders', 'Total Spend', 'Green Spend']);
    (data.by_delivery_location || []).forEach((r) => rows.push([r.name, r.address, r.city || '', r.state || '', r.zip || '', r.order_count, r.total_spend, r.green_spend]));

    const csv = rows.map((r) => r.map((c) => {
      const s = c == null ? '' : String(c);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `uske-spend-${label.replace(/\s+/g, '_')}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast({ title: 'CSV exported' });
  };

  const years = useMemo(() => {
    const arr = []; for (let y = currentYear - 3; y <= currentYear + 1; y++) arr.push(y); return arr;
  }, [currentYear]);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  return (
    <div className="space-y-4" data-testid="reports-tab">
      {/* Controls */}
      <Card className="border-2">
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">Period</Label>
            <div className="flex gap-1 mt-1">
              <Button size="sm" variant={period === 'monthly' ? 'default' : 'outline'} onClick={() => setPeriod('monthly')} data-testid="period-monthly" className={period === 'monthly' ? 'bg-blue-700 hover:bg-blue-800 text-white' : ''}>Monthly</Button>
              <Button size="sm" variant={period === 'quarterly' ? 'default' : 'outline'} onClick={() => setPeriod('quarterly')} data-testid="period-quarterly" className={period === 'quarterly' ? 'bg-blue-700 hover:bg-blue-800 text-white' : ''}>Quarterly</Button>
            </div>
          </div>
          <div>
            <Label className="text-xs">Year</Label>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="border rounded px-2 py-1.5 text-sm bg-white mt-1" data-testid="year-select">
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          {period === 'monthly' ? (
            <div>
              <Label className="text-xs">Month</Label>
              <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="border rounded px-2 py-1.5 text-sm bg-white mt-1" data-testid="month-select">
                {months.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
            </div>
          ) : (
            <div>
              <Label className="text-xs">Quarter</Label>
              <select value={quarter} onChange={(e) => setQuarter(Number(e.target.value))} className="border rounded px-2 py-1.5 text-sm bg-white mt-1" data-testid="quarter-select">
                {[1, 2, 3, 4].map((q) => <option key={q} value={q}>Q{q}</option>)}
              </select>
            </div>
          )}
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="report-refresh"><RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh</Button>
            <Button size="sm" onClick={exportCsv} disabled={!data} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="report-export-csv"><Download className="h-4 w-4 mr-1" /> Export CSV</Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-700" /></div>
      ) : data ? (
        <>
          {/* Totals cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Card className="border-2"><CardContent className="p-4">
              <div className="text-xs uppercase text-slate-500">Orders · {data.label}</div>
              <div className="text-2xl font-bold text-slate-800 mt-1" data-testid="totals-orders">{data.totals.order_count}</div>
            </CardContent></Card>
            <Card className="border-2"><CardContent className="p-4">
              <div className="text-xs uppercase text-slate-500">Total Spend</div>
              <div className="text-2xl font-bold text-slate-800 mt-1" data-testid="totals-spend">{money(data.totals.total_spend)}</div>
            </CardContent></Card>
            <Card className="border-2 border-green-300 bg-green-50/40"><CardContent className="p-4">
              <div className="text-xs uppercase text-green-800 flex items-center gap-1"><Leaf className="h-3.5 w-3.5" /> Total Green Spend</div>
              <div className="text-2xl font-bold text-green-800 mt-1" data-testid="totals-green">{money(data.totals.green_spend)}</div>
              <div className="text-[11px] text-green-700 mt-0.5">{data.totals.green_pct}% of total</div>
            </CardContent></Card>
          </div>

          {/* By Department */}
          <Card className="border-2"><CardHeader><CardTitle className="text-base flex items-center gap-2"><Layers className="h-4 w-4 text-slate-500" /> Spend by Department</CardTitle></CardHeader>
            <CardContent>
              {(data.by_department || []).length === 0 ? <div className="text-xs text-slate-400 py-2">No spend in this period.</div> : (
                <Table headers={['Division', 'Department', 'Orders', 'Total Spend', 'Green Spend']}>
                  {data.by_department.map((r, i) => (
                    <tr key={r.department_id + i} className="border-t" data-testid={`report-dept-${r.department_id}`}>
                      <td className="p-2 text-slate-600">{r.division_name || '—'}</td>
                      <td className="p-2 font-medium">{r.department_name}</td>
                      <td className="p-2 tabular-nums">{r.order_count}</td>
                      <td className="p-2 tabular-nums font-semibold">{money(r.total_spend)}</td>
                      <td className="p-2 tabular-nums text-green-700">{money(r.green_spend)}</td>
                    </tr>
                  ))}
                </Table>
              )}
            </CardContent>
          </Card>

          {/* By Cost Center */}
          <Card className="border-2"><CardHeader><CardTitle className="text-base flex items-center gap-2"><Wallet className="h-4 w-4 text-slate-500" /> Spend by Cost Center</CardTitle></CardHeader>
            <CardContent>
              {(data.by_cost_center || []).length === 0 ? <div className="text-xs text-slate-400 py-2">No spend in this period.</div> : (
                <Table headers={['Code', 'Name', 'GL Account', 'Orders', 'Total Spend', 'Green Spend']}>
                  {data.by_cost_center.map((r, i) => (
                    <tr key={r.cost_center_id + i} className="border-t" data-testid={`report-cc-${r.cost_center_id}`}>
                      <td className="p-2 font-mono text-xs">{r.code}</td>
                      <td className="p-2 font-medium">{r.name}</td>
                      <td className="p-2 text-xs text-slate-500">{r.gl_account || '—'}</td>
                      <td className="p-2 tabular-nums">{r.order_count}</td>
                      <td className="p-2 tabular-nums font-semibold">{money(r.total_spend)}</td>
                      <td className="p-2 tabular-nums text-green-700">{money(r.green_spend)}</td>
                    </tr>
                  ))}
                </Table>
              )}
            </CardContent>
          </Card>

          {/* By Delivery Location */}
          <Card className="border-2"><CardHeader><CardTitle className="text-base flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-500" /> Spend by Delivery Location</CardTitle></CardHeader>
            <CardContent>
              {(data.by_delivery_location || []).length === 0 ? <div className="text-xs text-slate-400 py-2">No spend in this period.</div> : (
                <Table headers={['Name', 'Address', 'Orders', 'Total Spend', 'Green Spend']}>
                  {data.by_delivery_location.map((r, i) => (
                    <tr key={r.location_id + i} className="border-t" data-testid={`report-loc-${r.location_id}`}>
                      <td className="p-2 font-medium">{r.name}</td>
                      <td className="p-2 text-xs text-slate-500">{r.address || '—'}</td>
                      <td className="p-2 tabular-nums">{r.order_count}</td>
                      <td className="p-2 tabular-nums font-semibold">{money(r.total_spend)}</td>
                      <td className="p-2 tabular-nums text-green-700">{money(r.green_spend)}</td>
                    </tr>
                  ))}
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      ) : (
        <div className="text-slate-400 text-sm">No report available.</div>
      )}
    </div>
  );
};


// ============================================================================
// Shared UI helpers
// ============================================================================
const Table = ({ headers, children }) => (
  <div className="border rounded overflow-hidden">
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
        <tr>{headers.map((h) => <th key={h} className="text-left p-2">{h}</th>)}</tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

const Field = ({ label, children }) => (
  <div><Label className="text-xs">{label}</Label>{children}</div>
);

const FormDialog = ({ title, onClose, onSave, children }) => (
  <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
    <Card className="w-full max-w-lg bg-white max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
      <CardHeader className="border-b flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">{title}</CardTitle>
        <Button size="sm" variant="ghost" onClick={onClose}><X className="h-4 w-4" /></Button>
      </CardHeader>
      <CardContent className="p-4 space-y-3 overflow-y-auto">{children}</CardContent>
      <div className="p-3 border-t flex justify-end gap-2 bg-slate-50">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button className="bg-blue-700 hover:bg-blue-800 text-white" onClick={onSave} data-testid="dlg-save"><Save className="h-4 w-4 mr-1" /> Save</Button>
      </div>
    </Card>
  </div>
);

export default GovGovernance;
